import { FormEvent, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Drawer } from './Drawer';
import { Field, TextInput } from './Field';
import { storageApi, type StorageHealth, type SystemSettings } from '@api/storage';
import { HttpError } from '@api/http';

interface Props {
  open: boolean;
  onClose: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

export function GlobalSettingsDrawer({ open, onClose }: Props): JSX.Element | null {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [health, setHealth] = useState<StorageHealth | null>(null);
  const [retentionDays, setRetentionDays] = useState('14');
  const [thresholdPercent, setThresholdPercent] = useState('90');
  const [emergencyDays, setEmergencyDays] = useState('7');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [runningCleanup, setRunningCleanup] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [settingsRes, healthRes] = await Promise.all([
        storageApi.getSettings(),
        storageApi.getHealth(),
      ]);
      setSettings(settingsRes);
      setHealth(healthRes);
      setRetentionDays(String(settingsRes.retentionDays));
      setThresholdPercent(String(settingsRes.storageFullThresholdPercent));
      setEmergencyDays(String(settingsRes.emergencyRetentionDays));
    } catch (err) {
      const message =
        err instanceof HttpError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load settings';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setStatusMessage(null);
    void load();
  }, [open, load]);

  if (!open) return null;

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const days = Number(retentionDays);
    const threshold = Number(thresholdPercent) / 100;
    const emergency = Number(emergencyDays);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      setError('Retention days must be between 1 and 365.');
      return;
    }
    if (!Number.isFinite(threshold) || threshold < 0.5 || threshold > 0.99) {
      setError('Storage full threshold must be between 50% and 99%.');
      return;
    }
    if (!Number.isFinite(emergency) || emergency < 1 || emergency > 90) {
      setError('Emergency keep days must be between 1 and 90.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setStatusMessage(null);
    try {
      const saved = await storageApi.updateSettings({
        retentionDays: days,
        storageFullThreshold: threshold,
        emergencyRetentionDays: emergency,
      });
      setSettings(saved);
      const healthRes = await storageApi.getHealth();
      setHealth(healthRes);
      setStatusMessage('Settings saved.');
    } catch (err) {
      const message =
        err instanceof HttpError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to save settings';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRunCleanup = async (): Promise<void> => {
    const confirmed = window.confirm(
      'Delete ALL recordings now? This removes today\'s footage and cannot be undone.',
    );
    if (!confirmed) return;

    setRunningCleanup(true);
    setError(null);
    setStatusMessage(null);
    try {
      const result = await storageApi.runRetention();
      const healthRes = await storageApi.getHealth();
      setHealth(healthRes);
      setStatusMessage(
        `Cleanup finished (${result.mode}): deleted ${result.deletedFiles} file(s), freed ${formatBytes(result.deletedBytes)}.`,
      );
    } catch (err) {
      const message =
        err instanceof HttpError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Retention cleanup failed';
      setError(message);
    } finally {
      setRunningCleanup(false);
    }
  };

  const sourceHint = (source: 'database' | 'environment'): string =>
    source === 'environment' ? 'Using .env default until saved here.' : 'Saved in database.';

  return (
    <Drawer
      open={open}
      title="Global settings"
      description="Recording retention, disk usage thresholds, and emergency cleanup when storage is full."
      onClose={submitting || runningCleanup ? () => undefined : onClose}
      widthClass="w-full max-w-lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleRunCleanup}
            disabled={loading || submitting || runningCleanup}
            className={clsx(
              'rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-foreground',
              'hover:border-accent disabled:opacity-50',
            )}
          >
            {runningCleanup ? 'Deleting all…' : 'Delete all recordings'}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting || runningCleanup}
              className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-foreground hover:border-accent disabled:opacity-50"
            >
              Close
            </button>
            <button
              type="submit"
              form="global-settings-form"
              disabled={loading || submitting || runningCleanup}
              className="rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </div>
      }
    >
      <form id="global-settings-form" className="flex flex-col gap-4" onSubmit={handleSubmit}>
        {error ? (
          <p role="alert" className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
        {statusMessage ? (
          <p className="rounded-md border border-accent/30 bg-accent/10 px-3 py-2 text-sm text-foreground">
            {statusMessage}
          </p>
        ) : null}

        {loading ? (
          <p className="text-sm text-muted">Loading settings…</p>
        ) : (
          <>
            <section className="flex flex-col gap-3 rounded-md border border-border bg-surface-2/40 p-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted">Storage usage</h3>
              {health?.volumes.map((volume) => (
                <div key={volume.path} className="text-xs text-foreground">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{volume.label}</span>
                    <span className={volume.reachable ? '' : 'text-danger'}>
                      {volume.reachable ? `${volume.usagePercent}% used` : 'Unreachable'}
                    </span>
                  </div>
                  {volume.reachable ? (
                    <p className="mt-0.5 text-muted">
                      {formatBytes(volume.usedBytes)} / {formatBytes(volume.totalBytes)} free{' '}
                      {formatBytes(volume.availableBytes)}
                    </p>
                  ) : (
                    <p className="mt-0.5 text-muted">{volume.error ?? volume.path}</p>
                  )}
                </div>
              ))}
              {health?.overThreshold ? (
                <p className="text-xs text-warning">
                  Storage is at or above {health.thresholdPercent}% — emergency retention keeps only the last{' '}
                  {emergencyDays} day(s) of recordings.
                </p>
              ) : null}
            </section>

            <p className="text-xs text-muted">
              &quot;Delete all recordings&quot; removes every file immediately. Nightly cleanup uses retention days;
              when storage is over the threshold, only the emergency keep period is kept.
            </p>

            <Field
              label="Retention period (days)"
              hint={`Delete recordings older than this age during nightly cleanup (when storage is below the threshold). ${settings ? sourceHint(settings.sources.retentionDays) : ''}`}
            >
              {(id) => (
                <TextInput
                  id={id}
                  type="number"
                  min={1}
                  max={365}
                  value={retentionDays}
                  onChange={(e) => setRetentionDays(e.target.value)}
                  disabled={submitting}
                />
              )}
            </Field>

            <Field
              label="Storage full threshold (%)"
              hint={`When any recording volume reaches this usage, automatic cleanup keeps only the emergency keep period below. ${settings ? sourceHint(settings.sources.storageFullThreshold) : ''}`}
            >
              {(id) => (
                <TextInput
                  id={id}
                  type="number"
                  min={50}
                  max={99}
                  step={1}
                  value={thresholdPercent}
                  onChange={(e) => setThresholdPercent(e.target.value)}
                  disabled={submitting}
                />
              )}
            </Field>

            <Field
              label="Emergency keep period (days)"
              hint={`When storage is full, delete everything older than this window. ${settings ? sourceHint(settings.sources.emergencyRetentionDays) : ''}`}
            >
              {(id) => (
                <TextInput
                  id={id}
                  type="number"
                  min={1}
                  max={90}
                  value={emergencyDays}
                  onChange={(e) => setEmergencyDays(e.target.value)}
                  disabled={submitting}
                />
              )}
            </Field>
          </>
        )}
      </form>
    </Drawer>
  );
}
